import { IonIcon, IonModal, IonPage } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  checkmarkCircleOutline,
  closeCircleOutline,
  documentTextOutline,
  navigateOutline,
  pencilOutline,
} from 'ionicons/icons'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'urql'
import { Markdown } from '../components/Markdown'
import { LinesOnSiteCallout } from '../components/order/LinesOnSiteCallout'
import {
  PourDetailsRow,
  PourScheduleRow,
  SiteRow,
} from '../components/order/OrderDetailFieldRows'
import { OrderPhotosRow } from '../components/order/OrderPhotosRow'
import { PreWorkAuthorizationSection } from '../components/order/PreWorkAuthorizationSection'
import { useShortLinkContext } from '../context/ShortLinkContext'
import { GetOrderDetail } from '../graphql/orders'
import { parseDatetime } from '../helpers/datetime'
import { formatNameWithSecondary } from '../helpers/formatNameWithSecondary'
import { formatPhone } from '../helpers/formatPhone'
import { useFeature } from '../hooks/useFeatures'
import { useNavigatePress } from '../hooks/useNavigate'
import { StandardLayout } from '../layouts/StandardLayout'
import { CancelOrderModal } from '../modals/CancelOrderModal'
import { CustomerSignatureModal } from '../modals/CustomerSignatureModal'
import { WorkTicketModal } from '../modals/WorkTicketModal'

export const OrderDetailPage = () => {
  const { _ } = useLingui()
  const { assignmentId, orderId } = useParams<{
    assignmentId?: string
    orderId: string
  }>()

  const orderIdNum = Number.parseInt(orderId, 10)

  const [result, reexecute] = useQuery({
    query: GetOrderDetail,
    variables: { orderId: orderIdNum },
    // Serve the prefetched/normalized cache instantly on (re)mount so back
    // navigation doesn't flash "Loading…" (Ionic remounts outlet pages).
    // Mutations already refetch network-only to stay fresh.
    requestPolicy: 'cache-first',
  })

  const order = result.data?.order

  // Tap navigates with the saved app; long-press re-picks the app (v1 parity)
  const siteAddresses = order?.site?.address ? [order.site.address] : []
  const headerNavRef = useNavigatePress<HTMLButtonElement>(siteAddresses)
  const siteNavRef = useNavigatePress<HTMLButtonElement>(siteAddresses)

  // Modal visibility state
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showWorkTicketModal, setShowWorkTicketModal] = useState(false)
  const [showSignatureModal, setShowSignatureModal] = useState(false)

  // Standalone /orders/:orderId (v1 parity) has no assignment context
  const backPath = assignmentId
    ? `/assignments/${assignmentId}`
    : '/assignments'

  // Derive status flags
  const statusSlug = order?.statusDetails?.slug || order?.status || ''
  const isCancelled = statusSlug === 'cancelled'
  const allowOperatorToCancel = useFeature('orderAllowOperatorToCancel')
  const useCubicMeters = useFeature('localeUseCubicMeters')
  // No photos in the short-link mini-app: upload needs the main session
  const isInShortLinkApp = useShortLinkContext() !== null
  const orderPhotosEnabled = useFeature('orderPhotosEnabled')
  const workTicketNeeded = !['turned_in', 'reviewed', 'cancelled'].includes(
    statusSlug,
  )
  const signatureNeeded = order?.terms?.needsAcceptance === true
  const signatureCollected = order?.terms?.accepted === true

  // Show the signature button whenever terms exist (needsAcceptance or
  // accepted). NOT gated by isOnline — collecting a signature must work offline;
  // the mutation queues and the optimistic update advances the UI.
  const showSignature = signatureCollected || signatureNeeded

  // Schedule helpers
  const schedule = order?.planned?.schedule ?? []
  const pourSteps = schedule.filter((s) => s.step === 'pour')
  const hasMultiplePours = pourSteps.length > 1

  // First schedule start time as "onsite time"
  const onsiteTime = schedule[0]?.startTime
    ? parseDatetime(schedule[0].startTime, order?.site?.address?.timezone)
    : null

  // Field contacts — mirrors v1's contactsForRole helper:
  // prefer contacts with role.slug === 'field', fall back to default contacts
  const allContacts = order?.contacts ?? []
  const byRole = allContacts.filter((oc) => oc.role?.slug === 'field')
  const fieldContacts =
    byRole.length > 0 ? byRole : allContacts.filter((oc) => oc.default)

  // Hide inventory the office marked as not-for-crew (v1's filterVisibleInventory).
  const visibleInventory = (order?.planned?.inventory ?? []).filter(
    (inv) => inv.item?.visibility?.operatorAssignment !== 'hidden',
  )

  // The server answered that there is no such order (v1 "Not Found" screen).
  // An offline cache miss answers `data: null` with no error, so key on the
  // order being null, not on the order being absent.
  const isOrderNotFound =
    !result.fetching && !result.error && result.data?.order === null
  const isUnavailableOffline =
    !result.fetching && !result.error && result.data == null

  const title = isOrderNotFound
    ? _(msg({ message: 'Not Found', context: 'order' }))
    : order?.site?.name || order?.customer?.name || order?.name || _(msg`Order`)

  const dismissAndRefresh = (setter: (v: boolean) => void) => {
    setter(false)
    reexecute({ requestPolicy: 'network-only' })
  }

  return (
    <IonPage>
      <StandardLayout
        title={title}
        defaultBackTo={backPath}
        headerEndButtons={
          order?.site?.address?.lat != null &&
          order?.site?.address?.lng != null ? (
            <button
              type='button'
              ref={headerNavRef}
              className='assignment-detail__nav-btn'
              aria-label={_(msg`Navigate to Site`)}
            >
              <IonIcon icon={navigateOutline} />
            </button>
          ) : undefined
        }
      >
        {result.fetching && !order && (
          <div className='order-detail__loading'>{_(msg`Loading...`)}</div>
        )}

        {result.error && !order && (
          <div className='order-detail__error'>{result.error.message}</div>
        )}

        {isOrderNotFound && (
          <div className='order-detail__error'>
            {_(msg`This order could not be found`)}
          </div>
        )}

        {isUnavailableOffline && (
          <div className='order-detail__error'>
            {_(msg`This order is not available offline`)}
          </div>
        )}

        {order && (
          <div className='order-detail'>
            {/* Cancelled banner */}
            {isCancelled && (
              <div className='order-detail__cancelled-banner'>
                {_(msg`Cancelled`)}
              </div>
            )}

            {/* Lines on site safety warning (v1) */}
            <LinesOnSiteCallout linesOnSite={order.linesOnSite} />

            {/* Pre-work authorization status */}
            <PreWorkAuthorizationSection orderId={orderIdNum} />

            {/* Main info card */}
            <div className='order-detail__card'>
              <SiteRow
                site={order.site}
                onsiteTime={onsiteTime}
                navRef={siteNavRef}
              />

              {/* Instructions */}
              {order.instructions && (
                <div className='order-detail__row order-detail__row--full'>
                  <div className='order-detail__col'>
                    <div className='order-detail__label'>
                      {_(msg`Instructions`)}
                    </div>
                    <div className='order-detail__value order-detail__markdown'>
                      <Markdown>{order.instructions}</Markdown>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer + Contact */}
              <div className='order-detail__row'>
                {order.customer && (
                  <div className='order-detail__col order-detail__col--wide'>
                    <div className='order-detail__label'>
                      {_(msg`Customer`)}
                    </div>
                    <div className='order-detail__value'>
                      {order.customer.name}
                    </div>
                  </div>
                )}
                {fieldContacts.length > 0 && (
                  <div className='order-detail__col'>
                    <div className='order-detail__label'>{_(msg`Contact`)}</div>
                    {fieldContacts.map((oc) => (
                      <div key={oc.id}>
                        <div className='order-detail__value'>
                          {oc.contact.firstName} {oc.contact.lastName}
                        </div>
                        {oc.contact.phones.map((phone) => (
                          <a
                            key={phone.id}
                            href={`tel:${phone.number}`}
                            className='order-detail__phone-link'
                          >
                            {formatPhone(phone.number)}
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pour steps */}
              {pourSteps.map((pourStep, index: number) => {
                const pourYardage = pourStep.metrics?.find(
                  (m) => m.key === 'cubicYards',
                )?.value

                return (
                  <div key={pourStep.id || index}>
                    {hasMultiplePours && (
                      <div className='order-detail__row order-detail__row--full'>
                        <div className='order-detail__col'>
                          <div className='order-detail__label order-detail__label--pour'>
                            {_(msg`Pour`)} #{index + 1}
                          </div>
                        </div>
                      </div>
                    )}
                    <PourDetailsRow pour={pourStep.pour} />
                    <PourScheduleRow
                      pourTime={parseDatetime(
                        pourStep.startTime,
                        order.site?.address?.timezone,
                      )}
                      yardage={pourYardage}
                      yardageLabel={
                        useCubicMeters
                          ? _(msg`Cubic Meters`)
                          : _(msg`Pour Yardage`)
                      }
                    />
                  </div>
                )
              })}

              {/* Order # + Service */}
              {/* Inventory */}
              {visibleInventory.length > 0 && (
                <div className='order-detail__row order-detail__row--full'>
                  <div className='order-detail__col'>
                    <div className='order-detail__label'>
                      {_(msg`Inventory`)}
                    </div>
                    <ul className='order-detail__inventory-list'>
                      {visibleInventory.map((inv) => (
                        <li key={inv.id}>
                          {inv.item.name}
                          {inv.quantity
                            ? ` — ${inv.quantity}${inv.item.displayUnit || 'x'}`
                            : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Supplier */}
              {order.supplier && (
                <div className='order-detail__row order-detail__row--full'>
                  <div className='order-detail__col'>
                    <div className='order-detail__label'>
                      {_(msg`Supplier`)}
                    </div>
                    <div className='order-detail__value'>
                      {formatNameWithSecondary(
                        order.supplier.name,
                        order.supplier.name2,
                      )}
                    </div>
                    {order.supplier.phones?.[0]?.number && (
                      <a
                        href={`tel:${order.supplier.phones[0].number}`}
                        className='order-detail__phone-link'
                      >
                        {formatPhone(order.supplier.phones[0].number)}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Order # + Service */}
              <div className='order-detail__row'>
                <div className='order-detail__col order-detail__col--wide'>
                  <div className='order-detail__label'>
                    {_(msg`Order Number`)}
                  </div>
                  <div className='order-detail__value'>{order.id}</div>
                </div>
                {order.service && (
                  <div className='order-detail__col'>
                    <div className='order-detail__label'>{_(msg`Service`)}</div>
                    <div className='order-detail__value'>
                      {order.service.name}
                    </div>
                  </div>
                )}
              </div>

              {/* Purchase Order */}
              {order.purchaseOrder && (
                <div className='order-detail__row order-detail__row--full'>
                  <div className='order-detail__col'>
                    <div className='order-detail__label'>
                      {_(msg`Purchase Order`)}
                    </div>
                    <div className='order-detail__value'>
                      {order.purchaseOrder}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Photos (dedicated screen; row is the entry point) ── */}
            {orderPhotosEnabled && !isInShortLinkApp && (
              <OrderPhotosRow
                orderId={orderIdNum}
                assignmentId={assignmentId}
              />
            )}

            {/* ── Action Buttons ───────────────────────────────── */}
            <div className='order-detail__actions'>
              {/* Signature button */}
              {showSignature && (
                <button
                  type='button'
                  className={`order-detail__action-btn order-detail__action-btn--signature ${
                    signatureCollected
                      ? 'order-detail__action-btn--disabled'
                      : ''
                  }`}
                  onClick={() =>
                    !signatureCollected && setShowSignatureModal(true)
                  }
                  disabled={signatureCollected}
                >
                  <IonIcon
                    icon={
                      signatureCollected
                        ? checkmarkCircleOutline
                        : pencilOutline
                    }
                  />
                  {signatureCollected
                    ? _(msg`Signature Collected`)
                    : _(msg`Customer Signature`)}
                </button>
              )}

              {/* Work Ticket button */}
              {!isCancelled && (
                <button
                  type='button'
                  className={`order-detail__action-btn order-detail__action-btn--workticket ${
                    !workTicketNeeded
                      ? 'order-detail__action-btn--disabled'
                      : ''
                  }`}
                  onClick={() =>
                    workTicketNeeded && setShowWorkTicketModal(true)
                  }
                  disabled={!workTicketNeeded}
                >
                  <IonIcon
                    icon={
                      workTicketNeeded
                        ? documentTextOutline
                        : checkmarkCircleOutline
                    }
                  />
                  {workTicketNeeded
                    ? _(msg`Enter Work Ticket`)
                    : _(msg`Work Ticket Completed`)}
                </button>
              )}

              {/* Cancel Order — works offline too: the cancel goes through the
                  updateOrder mutation (status → cancelled), which has an
                  optimistic resolver, so it queues and the order flips locally. */}
              {!isCancelled && allowOperatorToCancel && (
                <button
                  type='button'
                  className='order-detail__cancel-btn'
                  onClick={() => setShowCancelModal(true)}
                >
                  <IonIcon icon={closeCircleOutline} />
                  {_(msg`Cancel Order`)}
                </button>
              )}

              {/* Collect on Delivery indicator (v1 parity) */}
              {order.cashOnDelivery && (
                <div className='order-detail__cod'>
                  <div className='order-detail__cod-title'>
                    {_(msg`Collect on Delivery`)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Inline Modals (inside provider tree) ─────────── */}
        <IonModal
          isOpen={showSignatureModal}
          onDidDismiss={() => dismissAndRefresh(setShowSignatureModal)}
        >
          <CustomerSignatureModal
            orderId={orderIdNum}
            onDismiss={() => dismissAndRefresh(setShowSignatureModal)}
          />
        </IonModal>

        <IonModal
          isOpen={showWorkTicketModal}
          onDidDismiss={() => dismissAndRefresh(setShowWorkTicketModal)}
        >
          <WorkTicketModal
            orderId={orderIdNum}
            onDismiss={() => dismissAndRefresh(setShowWorkTicketModal)}
          />
        </IonModal>

        <IonModal
          isOpen={showCancelModal}
          onDidDismiss={() => dismissAndRefresh(setShowCancelModal)}
        >
          <CancelOrderModal
            orderId={orderIdNum}
            onDismiss={() => dismissAndRefresh(setShowCancelModal)}
          />
        </IonModal>
      </StandardLayout>
    </IonPage>
  )
}

export default OrderDetailPage
